import type { WidgetInstance, WidgetMove } from "@salestrak/ui";

/**
 * The staged-mode save plan: diff the working copy against the canonical
 * baseline into the ordered operations `saveToTeam` commits — removes →
 * adds → replaces → configs → ONE move batch. Pure so the node test harness
 * can pin the plan's shape; the store owns the actual mutation sequencing
 * (and the temp→server id capture as adds land).
 */
export type WidgetBoardDiff = {
  /** Baseline rows gone from the working copy. */
  removes: string[];
  /** Working rows not in the baseline (local `temp-` ids — the store swaps
   *  in server ids as each add lands, so these never reach a mutation). */
  adds: WidgetInstance[];
  /** Surviving rows whose module changed — replace swaps type+config+schema
   *  in place, so a replaced row never ALSO appears in `configs`. */
  replaces: WidgetInstance[];
  /** Surviving same-type rows whose config JSON changed. */
  configs: WidgetInstance[];
  /** Every surviving rect change (replaced rows included — replace doesn't
   *  carry position). Added rows are absent: they land at their working
   *  position in the add itself. */
  moves: WidgetMove[];
};

export function diffWidgetBoards(
  baseline: WidgetInstance[],
  working: WidgetInstance[],
): WidgetBoardDiff {
  const baseById = new Map(baseline.map((i) => [i.id, i]));
  const workingIds = new Set(working.map((i) => i.id));

  const removes = baseline
    .filter((before) => !workingIds.has(before.id))
    .map((before) => before.id);

  const adds: WidgetInstance[] = [];
  const replaces: WidgetInstance[] = [];
  const configs: WidgetInstance[] = [];
  const moves: WidgetMove[] = [];
  for (const current of working) {
    const before = baseById.get(current.id);
    if (!before) {
      adds.push(current);
      continue;
    }
    if (before.type !== current.type) {
      replaces.push(current);
    } else if (
      JSON.stringify(before.config ?? {}) !== JSON.stringify(current.config ?? {})
    ) {
      configs.push(current);
    }
    if (
      before.x !== current.x ||
      before.y !== current.y ||
      before.w !== current.w ||
      before.h !== current.h
    ) {
      moves.push({ id: current.id, x: current.x, y: current.y, w: current.w, h: current.h });
    }
  }

  return { removes, adds, replaces, configs, moves };
}
